import useUpdateEffect from "ahooks/lib/useUpdateEffect";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { useAnalytics } from "../hooks/useAnalytics";
import { useScrollOnTransition } from "../hooks/useScrollManagement";
import { PreScreeningQuestion, QuestionType } from "../types/landingPage";
import { validateBMI as validateBMIUtil } from "../utils/bmiCalculator";
import { useQuestionnaireAnswersState } from "./contact-form/utils/questionnaireStorage";
import { DisqualificationScreen } from "./prescreening/DisqualificationScreen";
import { ProgressBar } from "./prescreening/ProgressBar";
import { QuestionRenderer } from "./prescreening/QuestionRenderer";
import { usePrescreeningState } from "./prescreening/utils/prescreeningStorage";
import { useCrossReferral } from "../hooks/useCrossReferral";
import useLandingPageIds from "../hooks/useLandingPageIds";
import { DEFAULT_SIGNUP_URL } from "../utils/urlConfig";

const resolveMinBMI = (
  minBMI: number | ((allAnswers: Record<string, string | string[]>) => number) | undefined,
  allAnswers: Record<string, string | string[]>,
): number | undefined => {
  if (typeof minBMI === "function") return minBMI(allAnswers);
  return minBMI;
};

const bmiInputSchema = z.object({
  height: z.string().min(1, "Height is required"),
  weight: z.string().min(1, "Weight is required"),
});

interface PreScreeningQuestionnaireProps {
  questions: PreScreeningQuestion[];
  onNextScreen: (answers?: Record<string, string | string[]>) => void;
  title?: string;
  subtitle?: string;
  landingPageId: string;
  hasMoreStudiesToTry?: boolean;
  isMultiStudyFlow?: boolean;
  hideProgressBarOnFirstQuestion?: boolean;
  hideProgressBarQuestionCount?: boolean;
}

export function PreScreeningQuestionnaire({
  questions,
  onNextScreen,
  title = "Pre-Screening Questionnaire",
  subtitle = "Please answer the following questions to see if you qualify for this clinical trial.",
  landingPageId,
  hasMoreStudiesToTry = false,
  isMultiStudyFlow = false,
  hideProgressBarOnFirstQuestion = false,
  hideProgressBarQuestionCount = false,
}: PreScreeningQuestionnaireProps) {
  const [_questionnaireAnswers, setQuestionnaireAnswers] =
    useQuestionnaireAnswersState();

  const questionnaireRef = useRef<HTMLDivElement>(null);

  const {
    questionnaireState: {
      currentQuestionIndex,
      answers,
      isDisqualified,
      bmi,
      error,
      disqualifyMessage,
    },
    updateAnswers,
    updateQuestionnareState,
    clearState,
  } = usePrescreeningState();

  const [disqualificationQuestionId, setDisqualificationQuestionId] = useState<
    string | undefined
  >(undefined);

  const disqualificationTrackedRef = useRef(false);

  const { track } = useAnalytics();
  
  // Get variant and master IDs for enhanced tracking
  const { variantId, masterId } = useLandingPageIds();
  const prescreenerId = questions[0]?.id ? `${landingPageId}_${questions[0].id}` : landingPageId;

  const crossReferral = useCrossReferral({
    landingPageId,
    questions,
    currentAnswers: answers,
    updateAnswers,
    updateQuestionnareState,
  });

  useUpdateEffect(() => {
    return () => {
      disqualificationTrackedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isDisqualified) {
      const currentQuestion = questions[currentQuestionIndex];
      if (currentQuestion) {
        track("lp.pre_screener.question_viewed", {
          question_num: currentQuestionIndex + 1,
          total_questions: questions.length,
          question_id: currentQuestion.id,
          landing_page_id:
            landingPageId && landingPageId.trim() ? landingPageId : "unknown",
          variant_id: variantId || landingPageId,
          master_id: masterId || landingPageId,
          prescreener_id: prescreenerId,
        });
      } else {
        // Pass current answers when auto-advancing due to no more questions
        onNextScreen(answers);
      }
    }
  }, [currentQuestionIndex, isDisqualified, questions, answers, onNextScreen]);

  const handleComplete = (
    answersToSave?: Record<string, string | string[]>,
  ) => {
    // Either pass in the answers to save or use current state
    const questionAnswers = answersToSave ?? answers;
    const currentStatus = isDisqualified ? "failed" : "passed";

    setQuestionnaireAnswers({
      questionAnswers,
      prescreeningStatus: currentStatus,
      bmi, // Include BMI for conditional trial routing
    });

    // Always track prescreener completion with eligibility status
    track("lp.pre_screener.completed", {
      is_eligible: !isDisqualified,
      total_questions: questions.length,
      questions_answered: Object.keys(answers).length,
      landing_page_id: landingPageId && landingPageId.trim() ? landingPageId : "unknown",
      variant_id: variantId || landingPageId,
      master_id: masterId || landingPageId,
      prescreener_id: prescreenerId,
    });

    // If disqualified and continuing, also track the specific disqualification event
    if (isDisqualified) {
      track("lp.pre_screener.disqualified_continue_to_contact_page", {
        disqualification_reason: disqualifyMessage,
        question_id: disqualificationQuestionId,
        landing_page_id: landingPageId && landingPageId.trim() ? landingPageId : "unknown",
        variant_id: variantId || landingPageId,
        master_id: masterId || landingPageId,
        prescreener_id: prescreenerId,
      });
    }
    clearState();

    // Pass both answers and status to avoid race conditions with async state updates
    // Store status in a special key that the handler can check
    onNextScreen({ ...questionAnswers, __prescreeningStatus: currentStatus });
  };

  // Handle disqualification with message
  const handleDisqualification = (message: string, questionId?: string) => {
    // If already disqualified, don't track again
    if (isDisqualified) {
      return;
    }

    updateQuestionnareState({
      isDisqualified: true,
      disqualifyMessage: message,
      error: message,
    });

    setDisqualificationQuestionId(questionId);

    // Only track if not already tracked
    if (!disqualificationTrackedRef.current) {
      // Track disqualification event
      track("lp.pre_screener.disqualified", {
        question_num: currentQuestionIndex + 1,
        total_questions: questions.length,
        question_id: questionId || questions[currentQuestionIndex].id,
        disqualification_reason: message,
        landing_page_id: landingPageId && landingPageId.trim() ? landingPageId : "unknown",
        variant_id: variantId || landingPageId,
        master_id: masterId || landingPageId,
        prescreener_id: prescreenerId,
      });

      // Mark as tracked
      disqualificationTrackedRef.current = true;
    }
  };

  const updateBMI = (heightValue: string, weightValue: string): void => {
    const weightQuestion = questions.find((q) => q.id === "weight");
    validateBMIUtil({
      heightValue,
      weightValue,
      minBMI: resolveMinBMI(weightQuestion?.minBMI, answers),
      maxBMI: weightQuestion?.maxBMI,
      minWeight: weightQuestion?.minWeight,
      maxWeight: weightQuestion?.maxWeight,
      onBmiCalculated: (calculatedBmi) => {
        updateQuestionnareState({
          bmi: calculatedBmi > 0 ? calculatedBmi : null,
        });
      },
    });
  };

  const validateUserBMI = (): boolean => {
    const weightQuestion = questions.find((q) => q.id === "weight");

    const { height: heightValue, weight: weightValue } = bmiInputSchema.parse({
      height: answers["height"],
      weight: answers["weight"],
    });

    const { isValid, message } = validateBMIUtil({
      heightValue,
      weightValue,
      minBMI: resolveMinBMI(weightQuestion?.minBMI, answers),
      maxBMI: weightQuestion?.maxBMI,
      minWeight: weightQuestion?.minWeight,
      maxWeight: weightQuestion?.maxWeight,
      onBmiCalculated: (calculatedBmi) => {
        updateQuestionnareState({
          bmi: calculatedBmi > 0 ? calculatedBmi : null,
        });
      },
    });

    if (!isValid && message) {
      handleDisqualification(message, "weight");
    }

    return isValid;
  };

  const shouldAutoAdvance = (value: string | string[], questionId: string) => {
    switch (questions[currentQuestionIndex].type) {
      case QuestionType.RADIO:
        return true;
      case QuestionType.RADIO_GRID:
        return false;
      case QuestionType.CHECKBOX:
        return false;
      case QuestionType.SELECT:
        return value !== "";
      case QuestionType.TEXT:
        // TEXT inputs should never auto-advance - user must click Continue
        return false;
      case QuestionType.NUMBER:
        // Never auto-advance for weight question
        if (questionId === "weight") {
          return false;
        }
        return value !== "";
      default:
        return false;
    }
  };

  // Handle answer updates
  const handleAnswer = (
    questionId: string,
    value: string | string[],
    disqualificationInfo?: {
      evaluatedDisqualify: boolean;
      disqualificationMessage?: string;
    },
  ) => {
    track("lp.pre_screener.answer_interaction", {
      question_num: currentQuestionIndex + 1,
      total_questions: questions.length,
      question_type: questions[currentQuestionIndex].type,
      question_id: questionId,
      interaction_type: Array.isArray(value)
        ? "checkbox_selection"
        : "value_selection",
      landing_page_id: landingPageId && landingPageId.trim() ? landingPageId : "unknown",
      variant_id: variantId || landingPageId,
      master_id: masterId || landingPageId,
      prescreener_id: prescreenerId,
    });

    const updatedAnswers = { ...answers, [questionId]: value };

    // Initialize a variable for BMI that might be updated
    let currentBmi = bmi; // Use the BMI from state as the default

    // Determine if BMI needs to be (re)calculated based on the current question
    // and if the necessary counterpart (height/weight) is already in updatedAnswers.
    const isHeightQuestion = questionId === "height";
    const isWeightQuestion = questionId === "weight";
    const heightForBmi = isHeightQuestion
      ? (value as string)
      : updatedAnswers?.["height"];
    const weightForBmi = isWeightQuestion
      ? (value as string)
      : updatedAnswers?.["weight"];

    if (heightForBmi && weightForBmi) {
      const weightQuestion = questions.find((q) => q.id === "weight");
      validateBMIUtil({
        heightValue: heightForBmi as string,
        weightValue: weightForBmi as string,
        minBMI: resolveMinBMI(weightQuestion?.minBMI, updatedAnswers),
        maxBMI: weightQuestion?.maxBMI,
        minWeight: weightQuestion?.minWeight,
        maxWeight: weightQuestion?.maxWeight,
        onBmiCalculated: (calculatedBmi) => {
          currentBmi = calculatedBmi > 0 ? calculatedBmi : null;
          // Also update the global BMI state if it changed
          if (bmi !== currentBmi) {
            updateQuestionnareState({ bmi: currentBmi });
          }
        },
      });
    } else if (
      isWeightQuestion &&
      (isNaN(parseFloat(value as string)) || parseFloat(value as string) === 0)
    ) {
      // Reset BMI if weight is being set to zero or invalid
      currentBmi = null;
      if (bmi !== currentBmi) {
        // Avoid unnecessary state update
        updateQuestionnareState({ bmi: currentBmi });
      }
    }

    // Create a comprehensive answers object for the disqualify function
    const answersForDisqualifyCheck = { ...updatedAnswers, bmi: currentBmi };

    updateAnswers(updatedAnswers);

    // --- Primary Disqualification Check (e.g., from RADIO_GRID cell) ---
    // For CHECKBOX questions, we don't disqualify immediately - wait for Continue button
    const isCheckboxQuestion = questions.find((q) => q.id === questionId)?.type === QuestionType.CHECKBOX;
    
    if (disqualificationInfo?.evaluatedDisqualify && !isCheckboxQuestion) {
      handleDisqualification(
        disqualificationInfo.disqualificationMessage || // Use message from grid cell if provided
          questions.find((q) => q.id === questionId)?.disqualifyMessage || // Fallback to question message
          "Based on your answers, you do not qualify for this clinical trial.",
        questionId,
      );
      return; // Stop further processing if disqualified by grid cell
    }

    // --- Secondary Disqualification Check (for standard options like RADIO, SELECT) ---
    // This part only runs if not already disqualified by a grid cell's direct evaluation.
    const question = questions.find((q) => q.id === questionId);
    const currentOptionValue = Array.isArray(value)
      ? value[value.length - 1]
      : value;
    const selectedOption = question?.options?.find(
      (opt) => opt.value === currentOptionValue,
    );

    let shouldBeDisqualifiedFromOption = false;
    let optionSpecificMessage = question?.disqualifyMessage;

    if (selectedOption?.disqualify !== undefined) {
      if (typeof selectedOption.disqualify === "function") {
        shouldBeDisqualifiedFromOption = selectedOption.disqualify(
          answersForDisqualifyCheck,
          value,
        );
      } else {
        shouldBeDisqualifiedFromOption = selectedOption.disqualify;
      }
      if (shouldBeDisqualifiedFromOption && selectedOption.disqualifyMessage) {
        optionSpecificMessage = selectedOption.disqualifyMessage;
      }
    }

    if (shouldBeDisqualifiedFromOption && !isCheckboxQuestion) {
      // Check if this option has a redirect to another landing page
      if (selectedOption && crossReferral.shouldRedirect(selectedOption, true)) {
        crossReferral.handleCrossReferralRedirect({
          targetLandingPageId: selectedOption.redirectToLandingPageId!,
          sourceAnswers: updatedAnswers,
          triggerQuestionId: questionId,
          triggerAnswerValue: currentOptionValue as string,
          sourceLandingPageId: landingPageId,
        });
        return;
      }
      
      // Otherwise, show standard disqualification
      handleDisqualification(
        optionSpecificMessage ||
          "Based on your answers, you do not qualify for this clinical trial.",
        questionId,
      );
      return;
    }

    // Note: The original BMI update logic specific to 'weight' question is now part of the consolidated BMI update above.
    // However, we need to ensure that if height is updated, and weight exists, BMI is also updated for the global state.
    if (isHeightQuestion && weightForBmi) {
      updateBMI(value as string, weightForBmi as string);
    }
    // If it was the weight question and height exists, updateBMI was already called by the consolidated logic if heightForBmi was present.
    // If weight becomes 0 or invalid, bmi is set to null by the consolidated logic.

    if (shouldAutoAdvance(value, questionId)) {
      if (currentQuestionIndex < questions.length - 1) {
        if (selectedOption?.skipNextQuestions) {
          const skipCount = typeof selectedOption.skipNextQuestions === 'function'
            ? selectedOption.skipNextQuestions(answers)
            : selectedOption.skipNextQuestions;

          const nextIndex = Math.min(
            currentQuestionIndex + 1 + skipCount,
            questions.length - 1,
          );
          updateQuestionnareState({ currentQuestionIndex: nextIndex });
        } else {
          updateQuestionnareState({
            currentQuestionIndex: currentQuestionIndex + 1,
          });
        }
      } else {
        handleComplete(updatedAnswers);
      }
    }
  };

  // Use the scroll management hook for question transitions
  useScrollOnTransition(
    questionnaireRef,
    [currentQuestionIndex, questions.length],
    { behavior: "smooth", block: "start" },
    true, // Skip initial scroll
  );

  const handleBack = () => {
    // If disqualified, just reset the disqualification state and stay on the current question
    if (isDisqualified) {
      updateQuestionnareState({
        isDisqualified: false,
        disqualifyMessage: "",
        error: undefined,
      });

      // Reset the disqualification tracking flag
      disqualificationTrackedRef.current = false;
      return;
    }

    // Normal back navigation logic for non-disqualified state
    if (currentQuestionIndex > 0) {
      const currentQuestion = questions[currentQuestionIndex];
      // Track back button click
      track("lp.pre_screener.back_clicked", {
        question_num: currentQuestionIndex + 1,
        total_questions: questions.length,
        question_id: currentQuestion.id,
        landing_page_id: landingPageId && landingPageId.trim() ? landingPageId : "unknown",
        variant_id: variantId || landingPageId,
        master_id: masterId || landingPageId,
        prescreener_id: prescreenerId,
      });

      // Get current question ID to remove from answers
      const currentQuestionId = questions[currentQuestionIndex].id;

      // Remove answer for current question and update state
      const newAnswers = { ...answers };
      delete newAnswers[currentQuestionId];

      updateAnswers(newAnswers);

      updateQuestionnareState({
        currentQuestionIndex: currentQuestionIndex - 1,
        error: undefined,
        isDisqualified: false,
        disqualifyMessage: "",
        // Reset BMI if going back from weight question
        bmi: questions[currentQuestionIndex].id === "weight" ? null : bmi,
      });

      disqualificationTrackedRef.current = false;
    }
  };

  const handleClearAnswers = () => {
    const currentQuestion = questions[currentQuestionIndex];

    track("lp.pre_screener.clear_answers_clicked", {
      question_num: currentQuestionIndex + 1,
      total_questions: questions.length,
      answers_count: Object.keys(answers).length,
      question_id: currentQuestion.id,
      landing_page_id: landingPageId && landingPageId.trim() ? landingPageId : "unknown",
      variant_id: variantId || landingPageId,
      master_id: masterId || landingPageId,
      prescreener_id: prescreenerId,
    });

    clearState();

    disqualificationTrackedRef.current = false;
  };

  const handleSeekOtherTrials = () => {
    // In multi-study flow, continue to next study instead of redirecting
    if (isMultiStudyFlow && hasMoreStudiesToTry) {
      handleComplete();
      return;
    }

    // Redirect disqualified users to HealthMatch's main app to search for other trials
    window.location.href = DEFAULT_SIGNUP_URL;
  };

  const handleNext = () => {
    const currentQuestion = questions[currentQuestionIndex];
    const hasAnswer = !!answers[currentQuestion.id];
    const isLastQuestion = currentQuestionIndex === questions.length - 1;

    const currentAnswer = answers[currentQuestion.id];
    track("lp.pre_screener.continue_clicked", {
      question_num: currentQuestionIndex + 1,
      total_questions: questions.length,
      has_answer: hasAnswer,
      is_last_question: isLastQuestion,
      progress_percentage: Math.round(
        ((currentQuestionIndex + 1) / questions.length) * 100,
      ),
      question_id: currentQuestion.id,
      answer_value: Array.isArray(currentAnswer)
        ? `[${currentAnswer.length} selected]`
        : currentAnswer || null,
      landing_page_id: landingPageId && landingPageId.trim() ? landingPageId : "unknown",
      variant_id: variantId || landingPageId,
      master_id: masterId || landingPageId,
      prescreener_id: prescreenerId,
    });

    // Fire medication count discrepancy event when advancing past the medication checklist
    if (currentQuestion.id === "diabetes_medications_count" && answers["diabetes_medications_self_report"]) {
      const checkboxSelections = (Array.isArray(currentAnswer) ? currentAnswer : [])
        .filter((v: string) => v !== "none_diabetes_meds");
      const checkboxCount = checkboxSelections.length;
      const selfReportValue = answers["diabetes_medications_self_report"] as string;
      const radioCountMap: Record<string, number> = {
        no_medications: 0,
        one_medication: 1,
        two_medications: 2,
        three_or_more: 3,
      };
      const radioCount = radioCountMap[selfReportValue] ?? -1;

      const isThreeOrMoreSatisfied = radioCount === 3 && checkboxCount >= 3;
      track("lp.pre_screener.medication_count_discrepancy", {
        self_report_value: selfReportValue,
        self_report_count: radioCount,
        checklist_count: checkboxCount,
        discrepancy: isThreeOrMoreSatisfied ? 0 : checkboxCount - radioCount,
        is_discrepant: checkboxCount !== radioCount && !isThreeOrMoreSatisfied,
        question_id: currentQuestion.id,
        landing_page_id: landingPageId && landingPageId.trim() ? landingPageId : "unknown",
        variant_id: variantId || landingPageId,
        master_id: masterId || landingPageId,
        prescreener_id: prescreenerId,
      });
    }

    // For checkbox questions, check for disqualification when Continue is clicked
    if (currentQuestion.type === QuestionType.CHECKBOX) {
      const selectedValues = answers[currentQuestion.id] as string[] || [];
      const hasDisqualifyingOption = selectedValues.some(value => {
        const option = currentQuestion.options?.find(opt => opt.value === value);
        if (typeof option?.disqualify === 'function') {
          return option.disqualify({ ...answers, bmi }, selectedValues);
        }
        return option?.disqualify === true;
      });

      if (hasDisqualifyingOption) {
        handleDisqualification(
          currentQuestion.disqualifyMessage || 
          "Based on your answers, you do not qualify for this clinical trial.",
          currentQuestion.id
        );
        return;
      }
    }

    // Validate weight before advancing
    if (currentQuestion.id === "weight") {
      const weightValue = answers["weight"] as string;
      if (
        !weightValue ||
        weightValue.trim() === "" ||
        parseFloat(weightValue) === 0
      ) {
        updateQuestionnareState({
          error: "Please enter your weight to continue.",
        });
        return;
      }

      if (!validateUserBMI()) {
        return;
      }
    }

    if (currentQuestionIndex < questions.length - 1) {
      updateQuestionnareState({
        currentQuestionIndex: currentQuestionIndex + 1,
      });
      return;
    }
    handleComplete();
  };

  return (
    <div ref={questionnaireRef} className="w-full max-w-2xl mx-auto">
      {(title || subtitle) && (
        <div className="text-center mb-8">
          {title && <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>}
          {subtitle && <p className="text-gray-600">{subtitle}</p>}
        </div>
      )}

      {!isDisqualified ? (
        <div key={currentQuestionIndex} className="animate-fade-in">
          {!isMultiStudyFlow && !(hideProgressBarOnFirstQuestion && currentQuestionIndex === 0) && (
            <ProgressBar
              currentStep={currentQuestionIndex + 1}
              totalSteps={questions.length}
              hideQuestionCount={hideProgressBarQuestionCount}
            />
          )}

          <div className="mt-8">
            <QuestionRenderer
              question={questions[currentQuestionIndex]}
              value={
                answers[questions[currentQuestionIndex].id] ||
                (questions[currentQuestionIndex].type === QuestionType.CHECKBOX
                  ? []
                  : "")
              }
              allAnswers={answers}
              bmi={bmi}
              onAnswer={handleAnswer}
              onNext={handleNext}
              onBack={handleBack}
              onClearAnswers={handleClearAnswers}
              error={error}
              currentStep={currentQuestionIndex}
              totalSteps={questions.length}
              validateOnBlur={(_, weightValue) => {
                const heightValue = answers["height"];
                if (heightValue && weightValue) {
                  updateBMI(heightValue as string, weightValue);
                }
              }}
              landingPageId={landingPageId}
              variantId={variantId}
              masterId={masterId}
              prescreenerId={prescreenerId}
            />
          </div>
        </div>
      ) : (
        <DisqualificationScreen
          message={disqualifyMessage}
          onBack={handleBack}
          onSeekOtherTrials={handleSeekOtherTrials}
          questionId={disqualificationQuestionId}
          landingPageId={landingPageId}
          hasMoreStudiesToTry={hasMoreStudiesToTry}
          isMultiStudyFlow={isMultiStudyFlow}
        />
      )}
    </div>
  );
}
